import React, { useState, useEffect, useRef } from "react";
import DragDropArea from "./components/DragDropArea";
import FileList from "./components/FileList";
import SettingsModal from "./components/SettingsModal";
import { createFFmpeg, fetchFile } from "@ffmpeg/ffmpeg";
import jsmediatags from "jsmediatags";
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { isSupportedAudioFile, getUnsupportedFileMessage } from './utils/supportedAudioFormats';
import { createEmptyMetadata, isMetadataWritableOutputFormat } from './utils/metadataCapabilities';

function App() {
  const [files, setFiles] = useState([]);
  const [globalSettings, setGlobalSettings] = useState({
    format: "mp3",
    bitrate: "128k",
  });
  const [modalFileIndex, setModalFileIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentFileProgress, setCurrentFileProgress] = useState("");
  const [isFFmpegLoaded, setIsFFmpegLoaded] = useState(false);

  const ffmpegRef = useRef(null);

  useEffect(() => {
    if (ffmpegRef.current === null) {
      const loadFFmpeg = async () => {
        const ffmpeg = createFFmpeg({
          log: true,
          progress: ({ ratio }) => {
            const currentProgress = Math.floor(ratio * 100);
            setProgress(currentProgress);
          },
        });
        
        try {
          await ffmpeg.load();
          ffmpegRef.current = ffmpeg;
          setIsFFmpegLoaded(true);
          console.log("FFmpeg.wasm loaded successfully!");
        } catch (e) {
          console.error("Failed to load FFmpeg.wasm:", e);
          toast.error("コアコンポーネントの読み込みに失敗しました。ページをリロードしてください。");
        }
      };
      loadFFmpeg();
    }
  }, []);

  const outputFormats = ["mp3", "wav", "ogg", "flac", "aac", "aiff"];
  const bitrates = [
    { value: "128k", label: "128kbps (標準)" },
    { value: "192k", label: "192kbps (高音質)" },
    { value: "320k", label: "320kbps (最高音質)" },
  ];

  const handleFileDrop = (newFiles) => {
    const incomingFiles = Array.from(newFiles);
    const supportedFiles = incomingFiles.filter(isSupportedAudioFile);
    const rejectedFiles = incomingFiles.filter((file) => !isSupportedAudioFile(file));

    rejectedFiles.forEach((file) => {
      toast.warn(getUnsupportedFileMessage(file));
    });

    if (supportedFiles.length === 0) {
      return;
    }

    const currentFilesSize = files.reduce((acc, curr) => acc + curr.file.size, 0);
    const newFilesSize = supportedFiles.reduce((acc, curr) => acc + curr.size, 0);
    const totalSize = currentFilesSize + newFilesSize;
    const deviceMemoryInBytes = (navigator.deviceMemory || 2) * 1024 * 1024 * 1024;

    if (totalSize > deviceMemoryInBytes * 0.5) {
      toast.warn(`注意: ファイル合計サイズが非常に大きいです(${(totalSize / 1024 / 1024).toFixed(2)}MB)。ブラウザが不安定になる可能性があります。`);
    }

    const filePromises = supportedFiles.map((file) => {
      return new Promise((resolve) => {
        jsmediatags.read(file, {
          onSuccess: (tag) => {
            const metadata = {
              title: tag.tags.title || "",
              artist: tag.tags.artist || "",
              album: tag.tags.album || "",
            };
            resolve({
              file: file,
              settings: { ...globalSettings, trimStart: 0, trimEnd: null, volume: 1, normalize: false, metadata: metadata },
            });
          },
          onError: () => {
            resolve({
              file: file,
              settings: { ...globalSettings, trimStart: 0, trimEnd: null, volume: 1, normalize: false, metadata: createEmptyMetadata() },
            });
          },
        });
      });
    });

    Promise.all(filePromises).then((results) => {
      setFiles((prevFiles) => [...prevFiles, ...results]);
    });
  };

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileDrop(files);
    }
  };

  const handleGlobalSettingChange = (setting, value) => {
    setGlobalSettings((prev) => ({ ...prev, [setting]: value }));
    setFiles((prevFiles) => 
      prevFiles.map(fileItem => ({ ...fileItem, settings: { ...fileItem.settings, [setting]: value } }))
    );
  };

  const handleDeleteFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleClearFiles = () => {
    setFiles([]);
  };

  const handleCancel = () => {
    toast.info("変換処理は一度開始すると停止できません。ページを閉じるかリロードすると強制終了します。", { autoClose: 7000 });
  };

  const handleConvert = async () => {
    if (files.length === 0) {
      toast.warn("変換するファイルがありません。");
      return;
    }

    setIsConverting(true);
    const ffmpeg = ffmpegRef.current;

    if (!ffmpeg || !isFFmpegLoaded) {
      toast.warn("FFmpeg.wasmがまだ読み込まれていません。しばらくお待ちください。");
      setIsConverting(false);
      return;
    }
    
    setProgress(0);
    const zip = new JSZip();
    console.log("変換処理を開始します...");

    for (let i = 0; i < files.length; i++) {
      const { file, settings } = files[i];
      const originalFilename = file.name;
      const { metadata } = settings;
      let hasMetadata = false;
      const metadataFilename = `metadata_${i}.txt`;
      const metadataWritable = isMetadataWritableOutputFormat(settings.format);
      
      try {
        let metadataContent = ";FFMETADATA1\n";
        if (metadataWritable) {
          if (metadata.title) { metadataContent += `title=${metadata.title}\n`; hasMetadata = true; }
          if (metadata.artist) { metadataContent += `artist=${metadata.artist}\n`; hasMetadata = true; }
          if (metadata.album) { metadataContent += `album=${metadata.album}\n`; hasMetadata = true; }
        }

        if (hasMetadata) {
            await ffmpeg.FS('writeFile', metadataFilename, metadataContent);
        }

        const filenameWithoutExt = originalFilename.split(".").slice(0, -1).join(".");
        const newFilename = `${filenameWithoutExt}.${settings.format}`;
        const fileExt = originalFilename.split(".").pop();
        const inputFilename = `input_${i}.${fileExt}`;
        const outputFilename = `output_${i}.${settings.format}`;

        setCurrentFileProgress(`ファイル '${originalFilename}' を変換中... (${i + 1}/${files.length})`);
        await ffmpeg.FS('writeFile', inputFilename, await fetchFile(file));

        const command = [];
        if (settings.trimStart > 0) command.push("-ss", settings.trimStart.toString());
        if (settings.trimEnd && settings.trimEnd > settings.trimStart) command.push("-t", (settings.trimEnd - settings.trimStart).toString());
        
        command.push("-i", inputFilename);
        if (hasMetadata) command.push("-i", metadataFilename);

        const filterComplex = [];
        if (settings.volume !== 1) filterComplex.push(`volume=${settings.volume}`);
        if (settings.normalize) filterComplex.push("loudnorm");
        if (filterComplex.length > 0) command.push("-af", filterComplex.join(","));
        
        if (hasMetadata) {
            command.push("-map", "0:a");
            command.push("-map_metadata", "1");
        }
        
        const codecMap = { mp3: "libmp3lame", aac: "aac", wav: "pcm_s16le", ogg: "libvorbis", flac: "flac", aiff: "pcm_s16be" };
        const codec = codecMap[settings.format] || "copy";
        command.push("-c:a", codec);

        const isUncompressedOrLossless = settings.format === "wav" || settings.format === "flac" || settings.format === "aiff";
        if (codec !== "copy" && !isUncompressedOrLossless) {
          command.push("-b:a", settings.bitrate);
        }

        command.push(outputFilename);
        await ffmpeg.run(...command);
        const data = await ffmpeg.FS('readFile', outputFilename);
        
        zip.file(newFilename, data.buffer);

        await ffmpeg.FS('unlink', inputFilename);
        await ffmpeg.FS('unlink', outputFilename);
        if (hasMetadata) await ffmpeg.FS('unlink', metadataFilename);

        console.log(`ファイル '${originalFilename}' の変換が完了し、ZIPに追加しました。`);

      } catch (e) {
        toast.error(`'${originalFilename}'の変換中にエラーが発生しました。`);
        console.error(`ファイル '${originalFilename}' の変換中にエラーが発生しました:`, e);
      }
    }

    try {
        if (Object.keys(zip.files).length > 0) {
            const content = await zip.generateAsync({ type: "blob" });
            saveAs(content, "converted_files.zip");
            toast.success("全てのファイルの変換とZIP化が完了しました！");
        } else {
            toast.warn("変換に成功したファイルがなかったため、ZIPは作成されませんでした。");
        }
    } catch (e) {
        toast.error("ZIPファイルの生成に失敗しました。");
        console.error("ZIPファイルの生成に失敗しました:", e);
    }

    setIsConverting(false);
    setProgress(0);
    setCurrentFileProgress("");
  };

  const handleOpenModal = (index) => { setModalFileIndex(index); };
  const handleCloseModal = () => { setModalFileIndex(null); };
  
  const handleUpdateFileSettings = (index, newSettings) => {
    setFiles((prevFiles) =>
      prevFiles.map((item, i) =>
        i === index ? { ...item, settings: { ...item.settings, ...newSettings } } : item
      )
    );
  };

  const selectedFileObject = modalFileIndex !== null ? files[modalFileIndex] : null;

  return (
    <div
      className="App"
      style={{ padding: "20px" }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      
      <h1>音楽・音声かんたん変換ツール</h1>
      <p>音声を安全に、ブラウザ内で変換できるツールです。</p>

      <DragDropArea onFileDrop={handleFileDrop} isDragging={isDragging} />

      {files.length > 0 && (
        <>
          <div style={{ marginTop: "20px", maxWidth: "800px", margin: "20px auto" }}>
            <div style={{ display: "flex", justifyContent: "flex-start", gap: "20px", marginBottom: "15px", flexWrap: "wrap" }}>
              <div>
                <label htmlFor="cac-global-format" style={{ marginRight: "10px" }}>一括形式:</label>
                <select id="cac-global-format" name="global-format" onChange={(e) => handleGlobalSettingChange("format", e.target.value)} value={globalSettings.format} style={{ padding: "8px", borderRadius: "5px" }} disabled={isConverting}>
                  {outputFormats.map((format) => (<option key={format} value={format}>{format.toUpperCase()}</option>))}
                </select>
              </div>
              <div>
                <label htmlFor="cac-global-bitrate" style={{ marginRight: "10px" }}>一括音質:</label>
                <select id="cac-global-bitrate" name="global-bitrate" onChange={(e) => handleGlobalSettingChange("bitrate", e.target.value)} value={globalSettings.bitrate} style={{ padding: "8px", borderRadius: "5px" }} disabled={isConverting}>
                  {bitrates.map((bitrate) => (<option key={bitrate.value} value={bitrate.value}>{bitrate.label}</option>))}
                </select>
              </div>
            </div>
          </div>
          <FileList files={files} onOpenModal={handleOpenModal} onUpdateFileSettings={handleUpdateFileSettings} onDeleteFile={handleDeleteFile} onClearFiles={handleClearFiles} isConverting={isConverting} />

          <div style={{ textAlign: "right", marginTop: "20px", maxWidth: "800px", margin: "20px auto" }}>
            {isConverting && (
              <div style={{ marginBottom: "10px", textAlign: "left" }}>
                <p style={{marginBottom: "5px"}}>{currentFileProgress}</p>
                <progress value={progress} max="100" style={{ width: "100%", height: "20px" }}></progress>
                <p style={{marginTop: "5px"}}>全体進捗: {progress}%</p>
              </div>
            )}
            <button onClick={isConverting ? handleCancel : handleConvert} style={{ padding: "15px 30px", fontSize: "1.2rem", fontWeight: "bold", color: "#fff", backgroundColor: isConverting ? "#6c757d" : "#007bff", border: "none", borderRadius: "8px", cursor: "pointer", boxShadow: "0 4px 6px rgba(0,0,0,0.1)", opacity: !isFFmpegLoaded ? 0.6 : 1, transition: "background-color 0.2s" }} disabled={!isFFmpegLoaded}>
              {isConverting ? "変換中..." : `変換してZIPで保存 (${files.length}件)`}
            </button>
          </div>
        </>
      )}

      <SettingsModal key={modalFileIndex} isOpen={modalFileIndex !== null} onClose={handleCloseModal} file={selectedFileObject?.file} fileSettings={selectedFileObject?.settings} fileIndex={modalFileIndex} onUpdateFileSettings={handleUpdateFileSettings} isConverting={isConverting} ffmpeg={ffmpegRef.current} />
    </div>
  );
}

export default App;